/**
 * DirectoryTab — public-listing fields ONLY.
 *
 * Matches the existing PropertyEdit + DirectoryEditModal "Directory Listing"
 * section. Nothing else — no invoice linking, no certificates, no settings.
 *
 * view mode: images render as images, text fields render read-only, boolean
 *            flags render as "Yes/No" pills, amenities + listing links as
 *            badges + clickable rows. No edits.
 * edit mode: full form — same fields the old modal exposed, identical
 *            semantics. Save is handled by the modal footer.
 */

import { useRef, useState, type ChangeEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import ImageUpload, { BUCKET } from '../ImageUpload';
import { POS_GOLD, AMENITY_OPTIONS, PLATFORM_OPTIONS } from '../constants';
import type { EnrichedProperty, PropertyEditForm, PropertyMode } from './types';

interface ListingLink {
  platform: string;
  url: string;
  label?: string;
}

interface DirectoryTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
  form: PropertyEditForm;
  setForm: (updater: (f: PropertyEditForm) => PropertyEditForm) => void;
  mode: PropertyMode;
}

export default function DirectoryTab({ supabase, property, form, setForm, mode }: DirectoryTabProps) {
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const galleryFileRef = useRef<HTMLInputElement>(null);

  const isEdit = mode === 'edit';

  function toggleAmenity(value: string) {
    const current = form.amenity_tags || [];
    setForm(f => ({
      ...f,
      amenity_tags: current.includes(value)
        ? current.filter(t => t !== value)
        : [...current, value],
    }));
  }

  function addListingLink() {
    const links = (form.listing_links || []) as unknown as ListingLink[];
    setForm(f => ({
      ...f,
      listing_links: [...links, { platform: 'website', url: '', label: 'Website' }] as unknown as Array<Record<string, unknown>>,
    }));
  }

  function updateListingLink(idx: number, field: keyof ListingLink, value: string) {
    const links = [...((form.listing_links || []) as unknown as ListingLink[])];
    const current = links[idx];
    if (!current) return;
    const next: ListingLink = { ...current, [field]: value };
    if (field === 'platform') {
      const opt = PLATFORM_OPTIONS.find(p => p.value === value);
      if (opt) next.label = opt.label;
    }
    links[idx] = next;
    setForm(f => ({ ...f, listing_links: links as unknown as Array<Record<string, unknown>> }));
  }

  function removeListingLink(idx: number) {
    const links = (form.listing_links || []) as unknown as ListingLink[];
    setForm(f => ({ ...f, listing_links: links.filter((_, i) => i !== idx) as unknown as Array<Record<string, unknown>> }));
  }

  function addGalleryImage() {
    if (!newGalleryUrl.trim()) return;
    setForm(f => ({ ...f, gallery_images: [...(f.gallery_images || []), newGalleryUrl.trim()] }));
    setNewGalleryUrl('');
  }

  function removeGalleryImage(idx: number) {
    setForm(f => ({ ...f, gallery_images: (f.gallery_images || []).filter((_, i) => i !== idx) }));
  }

  // Upload a file straight into the gallery (same bucket + validation as the
  // hero/logo ImageUpload), appending its public URL to the gallery list. Each
  // upload gets a unique path so multiple images don't overwrite each other.
  async function handleGalleryUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!validTypes.includes(file.type)) {
      setGalleryError('Only JPEG, PNG, WebP, and AVIF images are accepted.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setGalleryError('File must be under 5 MB.');
      return;
    }
    setGalleryUploading(true);
    setGalleryError(null);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const storagePath = `${property.id}/gallery/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
      const publicUrl = `${urlData.publicUrl}?v=${Date.now()}`;
      setForm(f => ({ ...f, gallery_images: [...(f.gallery_images || []), publicUrl] }));
    } catch (err: unknown) {
      setGalleryError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setGalleryUploading(false);
      if (galleryFileRef.current) galleryFileRef.current.value = '';
    }
  }

  const listingLinks = (form.listing_links || []) as unknown as ListingLink[];
  const amenityLabel = (value: string) => AMENITY_OPTIONS.find(a => a.value === value)?.label || value;
  const platformLabel = (value: string) => PLATFORM_OPTIONS.find(p => p.value === value)?.label || value;

  // Shared visual shell for a read-only block (mirrors the Field wrapper elsewhere)
  function ReadBlock({ children }: { children: React.ReactNode }) {
    return (
      <div style={{
        padding: '0.5rem 0.75rem',
        background: 'var(--color-bg, #f9fafb)',
        border: '1px solid var(--color-border, #e5e7eb)',
        borderRadius: 'var(--radius-md)',
        minHeight: '2.25rem',
        display: 'flex',
        alignItems: 'center',
        fontSize: 'var(--text-lg)',
        color: children ? 'var(--color-text, #111827)' : 'var(--color-text-light, #9ca3af)',
      }}>
        {children || '—'}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Visibility */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>Visibility</h3>
        {isEdit ? (
          <>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_published}
                  onChange={(e) => setForm(f => ({ ...f, is_published: e.target.checked }))} />
                <span style={{ fontSize: 'var(--text-base)' }}>Published on peaceofsleep.vip</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_featured}
                  onChange={(e) => setForm(f => ({ ...f, is_featured: e.target.checked }))} />
                <span style={{ fontSize: 'var(--text-base)' }}>Featured listing</span>
              </label>
            </div>
            {form.is_published && (() => {
              const missing: string[] = [];
              if (!form.slug || !form.slug.trim()) missing.push('web address');
              if (!form.hero_image_url) missing.push('hero image');
              if (!form.tagline || !form.tagline.trim()) missing.push('tagline');
              if (missing.length === 0) return null;
              return (
                <div className="alert alert-warning mb-sm">
                  <span><strong>Not yet visible on the public site.</strong> Missing: {missing.join(', ')}.</span>
                </div>
              );
            })()}
          </>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <PillBadge on={form.is_published && !!form.slug && !!form.hero_image_url}
                       onLabel="Live on public site"
                       offLabel={form.is_published ? 'Published — not yet visible' : 'Not published'} />
            <PillBadge on={form.is_featured} onLabel="Featured" offLabel="Not featured" />
          </div>
        )}
      </section>

      {/* Web address */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>Web address</h3>
        <div className="form-group">
          <label className="form-label">URL handle</label>
          {isEdit ? (
            <>
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0 0.625rem',
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border, #e5e7eb)',
                  borderRight: 'none',
                  borderRadius: '6px 0 0 6px',
                  fontSize: 'var(--text-md)',
                  color: 'var(--color-text-light)',
                  fontFamily: 'monospace',
                }}>
                  peaceofsleep.vip/{(property.country || 'za').toLowerCase()}/{(property.province ? property.province.toLowerCase().replace(/[^a-z0-9]+/g, '-') : '…')}/
                </span>
                <input type="text" className="form-input" value={form.slug}
                  onChange={(e) => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '') }))}
                  placeholder="auto-filled from property name on save"
                  style={{ borderRadius: '0 6px 6px 0', flex: 1 }} />
              </div>
              <small style={{ color: 'var(--color-text-light)' }}>
                Leave blank to auto-generate from the property name. Once a property is live, avoid editing the handle — existing share links, QR codes and search results will stop working.
              </small>
            </>
          ) : (
            <ReadBlock>{form.slug || '— (will auto-generate on save)'}</ReadBlock>
          )}
        </div>
      </section>

      {/* Content */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>Content</h3>
        <div className="form-group">
          <label className="form-label">Tagline</label>
          {isEdit ? (
            <>
              <input type="text" className="form-input" value={form.tagline} maxLength={120}
                onChange={(e) => setForm(f => ({ ...f, tagline: e.target.value }))}
                placeholder="A peaceful retreat in tranquil Tokai" />
              <small style={{ color: 'var(--color-text-light)' }}>
                Short 1-liner for the hero section ({form.tagline.length}/120)
              </small>
            </>
          ) : (
            <ReadBlock>{form.tagline}</ReadBlock>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          {isEdit ? (
            <>
              <textarea className="form-input" value={form.description} rows={6} style={{ resize: 'vertical' }}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Multi-paragraph description of the property..." />
              <small style={{ color: 'var(--color-text-light)' }}>Separate paragraphs with blank lines</small>
            </>
          ) : (
            <div style={{
              padding: '0.75rem 1rem',
              background: 'var(--color-bg, #f9fafb)',
              border: '1px solid var(--color-border, #e5e7eb)',
              borderRadius: 'var(--radius-md)',
              whiteSpace: 'pre-wrap',
              minHeight: '4rem',
              fontSize: 'var(--text-lg)',
              color: form.description ? 'var(--color-text, #111827)' : 'var(--color-text-light, #9ca3af)',
            }}>
              {form.description || '—'}
            </div>
          )}
        </div>
      </section>

      {/* Images */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>Images</h3>

        {/* Hero */}
        <div className="form-group">
          <label className="form-label">Hero image</label>
          {isEdit ? (
            <ImageUpload
              propertyId={property.id}
              path="hero"
              currentUrl={form.hero_image_url}
              onUploaded={(url) => setForm(f => ({ ...f, hero_image_url: url }))}
              supabase={supabase}
            />
          ) : form.hero_image_url ? (
            <img src={form.hero_image_url} alt="Hero" style={{
              maxWidth: '100%', maxHeight: '220px', borderRadius: 'var(--radius)',
              border: '1px solid var(--color-border, #e5e7eb)', objectFit: 'cover',
            }} />
          ) : (
            <ReadBlock>{null}</ReadBlock>
          )}
        </div>

        {/* Logo */}
        <div className="form-group">
          <label className="form-label">Property logo</label>
          {isEdit ? (
            <ImageUpload
              propertyId={property.id}
              path="logo"
              currentUrl={form.brand_logo_url}
              onUploaded={(url) => setForm(f => ({ ...f, brand_logo_url: url }))}
              supabase={supabase}
            />
          ) : form.brand_logo_url ? (
            <img src={form.brand_logo_url} alt="Logo" style={{
              maxHeight: '80px', background: 'var(--color-white)',
              border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 'var(--radius)',
              padding: '0.5rem',
            }} />
          ) : (
            <ReadBlock>{null}</ReadBlock>
          )}
        </div>

        {/* Gallery */}
        <div className="form-group">
          <label className="form-label">Gallery images</label>
          {(form.gallery_images || []).length === 0 && !isEdit && <ReadBlock>{null}</ReadBlock>}
          {(form.gallery_images || []).map((url, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.375rem', alignItems: 'center' }}>
              {isEdit ? (
                <>
                  <input type="text" className="form-input" value={url} style={{ flex: 1 }}
                    onChange={(e) => {
                      const next = [...(form.gallery_images || [])];
                      next[idx] = e.target.value;
                      setForm(f => ({ ...f, gallery_images: next }));
                    }} />
                  <button type="button" className="btn btn-secondary"
                    style={{ padding: '0.375rem 0.5rem', fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}
                    onClick={() => removeGalleryImage(idx)}>Remove</button>
                </>
              ) : (
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--text-md)' }}>
                  {url}
                </a>
              )}
            </div>
          ))}
          {isEdit && (
            <>
              <div className="flex gap-sm">
                <input type="text" className="form-input flex-1" value={newGalleryUrl}
                  onChange={(e) => setNewGalleryUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGalleryImage(); } }}
                  placeholder="Add image URL..." />
                <button type="button" className="btn btn-secondary" style={{ whiteSpace: 'nowrap' }}
                  onClick={addGalleryImage}>Add</button>
                <label className="btn btn-secondary" style={{ whiteSpace: 'nowrap', cursor: galleryUploading ? 'wait' : 'pointer', opacity: galleryUploading ? 0.6 : 1 }}>
                  <input ref={galleryFileRef} type="file"
                    accept="image/jpeg,image/png,image/webp,image/avif"
                    onChange={handleGalleryUpload} disabled={galleryUploading}
                    style={{ display: 'none' }} />
                  {galleryUploading ? 'Uploading...' : 'Upload'}
                </label>
              </div>
              {galleryError && <div className="text-error text-sm mt-xs">{galleryError}</div>}
            </>
          )}
        </div>
      </section>

      {/* Public Contact */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>Public Contact</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Public email</label>
            {isEdit ? (
              <input type="email" className="form-input" value={form.public_email}
                onChange={(e) => setForm(f => ({ ...f, public_email: e.target.value }))}
                placeholder="guest@property.com" />
            ) : (
              <ReadBlock>{form.public_email}</ReadBlock>
            )}
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Public phone</label>
            {isEdit ? (
              <input type="tel" className="form-input" value={form.public_phone}
                onChange={(e) => setForm(f => ({ ...f, public_phone: e.target.value }))}
                placeholder="+27 21 123 4567" />
            ) : (
              <ReadBlock>{form.public_phone}</ReadBlock>
            )}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">WhatsApp number</label>
          {isEdit ? (
            <>
              <input type="tel" className="form-input" value={form.whatsapp_number}
                onChange={(e) => setForm(f => ({ ...f, whatsapp_number: e.target.value }))}
                placeholder="+27738706898" />
              <small style={{ color: 'var(--color-text-light)' }}>E.164 format with country code</small>
            </>
          ) : (
            <ReadBlock>{form.whatsapp_number}</ReadBlock>
          )}
        </div>
      </section>

      {/* Amenities */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>Amenities</h3>
        {isEdit ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.375rem' }}>
            {AMENITY_OPTIONS.map((amenity) => {
              const checked = (form.amenity_tags || []).includes(amenity.value);
              return (
                <label key={amenity.value} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  padding: '0.375rem 0.5rem',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-md)',
                  background: checked ? 'var(--color-success-bg)' : 'transparent',
                  border: checked ? '1px solid #bbf7d0' : '1px solid transparent',
                }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleAmenity(amenity.value)} />
                  {amenity.label}
                </label>
              );
            })}
          </div>
        ) : (form.amenity_tags || []).length === 0 ? (
          <ReadBlock>{null}</ReadBlock>
        ) : (
          <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
            {(form.amenity_tags || []).map(a => (
              <span key={a} className="badge badge-success">
                {amenityLabel(a)}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* External Listing Links */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>External listing links</h3>
        {listingLinks.length === 0 && !isEdit && <ReadBlock>{null}</ReadBlock>}
        {listingLinks.map((link, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
            {isEdit ? (
              <>
                <select className="form-input" value={link.platform} style={{ width: '140px', flex: 'none' }}
                  onChange={(e) => updateListingLink(idx, 'platform', e.target.value)}>
                  {PLATFORM_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <input type="text" className="form-input" value={link.url} style={{ flex: 1 }}
                  onChange={(e) => updateListingLink(idx, 'url', e.target.value)}
                  placeholder="https://..." />
                <input type="text" className="form-input" value={link.label || ''} style={{ width: '120px', flex: 'none' }}
                  onChange={(e) => updateListingLink(idx, 'label', e.target.value)}
                  placeholder="Label" />
                <button type="button" className="btn btn-secondary" style={{ padding: '0.375rem 0.5rem', fontSize: 'var(--text-sm)' }}
                  onClick={() => removeListingLink(idx)}>&times;</button>
              </>
            ) : (
              <div style={{
                flex: 1,
                padding: '0.5rem 0.75rem',
                background: 'var(--color-bg, #f9fafb)',
                border: '1px solid var(--color-border, #e5e7eb)',
                borderRadius: 'var(--radius-md)',
                display: 'flex', gap: '0.75rem', alignItems: 'center', fontSize: 'var(--text-base)',
              }}>
                <strong style={{ minWidth: '100px' }}>{platformLabel(link.platform)}</strong>
                <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
                  {link.url || '—'}
                </a>
              </div>
            )}
          </div>
        ))}
        {isEdit && (
          <button type="button" className="btn btn-secondary" style={{ fontSize: 'var(--text-md)' }}
            onClick={addListingLink}>+ Add Link</button>
        )}
      </section>

      {/* External Rating */}
      <section>
        <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginBottom: '0.75rem', color: POS_GOLD }}>External rating</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Rating</label>
            {isEdit ? (
              <input type="number" className="form-input" step="0.01" min="0" max="5"
                value={form.external_rating}
                onChange={(e) => setForm(f => ({ ...f, external_rating: e.target.value }))}
                placeholder="4.98" />
            ) : (
              <ReadBlock>{form.external_rating ? `${form.external_rating} / 5` : null}</ReadBlock>
            )}
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Source</label>
            {isEdit ? (
              <input type="text" className="form-input" value={form.external_rating_source}
                onChange={(e) => setForm(f => ({ ...f, external_rating_source: e.target.value }))}
                placeholder="Airbnb" />
            ) : (
              <ReadBlock>{form.external_rating_source}</ReadBlock>
            )}
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Review count</label>
            {isEdit ? (
              <input type="number" className="form-input" min="0" value={form.external_review_count}
                onChange={(e) => setForm(f => ({ ...f, external_review_count: e.target.value }))}
                placeholder="42" />
            ) : (
              <ReadBlock>{form.external_review_count}</ReadBlock>
            )}
          </div>
        </div>
      </section>

    </div>
  );
}

function PillBadge({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }) {
  return (
    <span className={`badge ${on ? 'badge-success' : 'badge-neutral'}`}>
      {on ? onLabel : offLabel}
    </span>
  );
}
